import {useContext, useEffect, useState} from 'react'
import {InlineConnectionContext, SELECT_INLINE_CONNECTION} from '../context/context'
import SelectInput from '../../../common/SelectInput'
import InputText from '../../../common/InputText'
import Button from '../../../common/Button'
import useDebouncedValue from '../../../../utils/common_function'
import {useParams} from 'react-router-dom'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {Col, Flex, Form, Modal, Row, Typography} from 'antd'
import {useIntl} from 'react-intl'

const InlineConnectionFormModal = (props) => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(InlineConnectionContext)
  const [, navDispatch] = useContext(NavContext)
  const [searchFailed, setSearchFailed] = useState({})

  const [form] = Form.useForm()

  const [fromConnectorOptions, setFromConnectorOptions] = useState([])
  const [toConnectorOptions, setToConnectorOptions] = useState([])

  const [fromConnectorLoading, setFromConnectorLoading] = useState(false)
  const [toConnectorLoading, setToConnectorLoading] = useState(false)

  const [searchFromConnector, setSearchFromConnector] = useState('')
  const [searchToConnector, setSearchToConnector] = useState('')

  const debouncedFromConnector = useDebouncedValue(searchFromConnector, 500)
  const debouncedToConnector = useDebouncedValue(searchToConnector, 500)

  useEffect(() => {
    if (!state.show_inline_popup) return

    fetchConnectorOptions('', 'from')
    fetchConnectorOptions('', 'to')
  }, [state.show_inline_popup])

  useEffect(() => {
    if (debouncedFromConnector !== undefined)
      fetchConnectorOptions(debouncedFromConnector, 'from')
  }, [debouncedFromConnector])

  useEffect(() => {
    if (debouncedToConnector !== undefined)
      fetchConnectorOptions(debouncedToConnector, 'to')
  }, [debouncedToConnector])

  const fetchConnectorOptions = async (search = '', field = '') => {
    const setLoading = field === 'from' ? setFromConnectorLoading : setToConnectorLoading
    const setOptions = field === 'from' ? setFromConnectorOptions : setToConnectorOptions
    setLoading(true)
    try {
      let res = await getRequest(
        `/api/inline_connection/connectors/?vehicle=${vehicle}&search=${search}&limit=10`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed((prev) => ({ ...prev, [field]: true }))
        setOptions([])
        return
      }
      setSearchFailed((prev) => ({ ...prev, [field]: false }))
      const options = (res.data || []).map((item) => ({
        value: item.id,
        label: item.name,
        ...item,
      }))
      setOptions(options)
    } finally {
      setLoading(false)
    }
  }

  const closeFormPopup = () => {
    dispatch({
      type: SELECT_INLINE_CONNECTION,
      data: { selected_inline_connection: {}, show_inline_popup: false },
    })
    form.resetFields()
  }

  const saveInlineConnection = async (values) => {
    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })
    formData.append('vehicle', vehicle)
    formData.append('import_file', state?.selected_inline_connection?.import_file || '')

    let res = null
    if (state?.selected_inline_connection?.id) {
      res = await putRequest(
        `/api/inline_connection/${state?.selected_inline_connection?.id}/?vehicle=${vehicle}`,
        formData
      )
    } else {
      res = await postRequest(`/api/inline_connection/?vehicle=${vehicle}`, formData)
    }

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: state?.selected_inline_connection?.id
            ? intl.formatMessage({ id: 'inlineConnections.updateSuccess' })
            : intl.formatMessage({ id: 'inlineConnections.saveSuccess' }),
        },
      })
      props.getListData()
      closeFormPopup()
    } else {
      let msg = ''
      Object.keys(res).map((obj) => {
        if (msg) {
          msg += `, ${res[obj]}`
        } else {
          msg = `${res[obj]}`
        }
      })
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: msg,
        },
      })
    }
  }

  const getInitialValues = () => {
    const inline = state?.selected_inline_connection || {}
    return {
      is_create: inline.id ? false : true,
      id: inline.id || null,
      import_file: inline.import_file || null,
      from_id: inline.from_id
        ? { value: inline.from_id.id, label: inline.from_id.name, ...inline.from_id }
        : null,
      to_id: inline.to_id
        ? { value: inline.to_id.id, label: inline.to_id.name, ...inline.to_id }
        : null,
      from_suffix: inline.from_suffix || null,
      to_suffix: inline.to_suffix || null,
      from_prefix: inline.from_prefix || null,
      to_prefix: inline.to_prefix || null,
    }
  }

  useEffect(() => {
    if (!form || !state.show_inline_popup) return
    form.setFieldsValue(getInitialValues())
  }, [form, state.show_inline_popup])

  return (
    <Modal
      open={state.show_inline_popup}
      title={intl.formatMessage({ id: 'inlineConnections.inlineDetails' })}
      width="50%"
      className="inline-connections-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="inline-connection-form"
          name="inline-connection-form"
          form={form}
          layout="vertical"
          onFinish={saveInlineConnection}
        >
          <InputText name="is_create" hidden={true} isRequired={false} />
          <InputText name="import_file" hidden={true} isRequired={false} />
          {state?.selected_inline_connection?.id && (
            <InputText
              name="id"
              hidden={true}
              isRequired={false}
              value={state?.selected_inline_connection?.id}
            />
          )}

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <SelectInput
                name="from_id"
                label={intl.formatMessage({ id: 'inlineConnections.fromConnector' })}
                options={fromConnectorOptions}
                notFoundContent={
                  searchFailed.from ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                placeholder={intl.formatMessage({ id: 'inlineConnections.selectFromConnector' })}
                isRequired={true}
                errorHelp={false}
                errorMsg={intl.formatMessage({ id: 'inlineConnections.fromConnectorRequired' })}
                allowClear={true}
                showSearch={true}
                showLoader={fromConnectorLoading}
                onInputChange={(value) => {
                  setSearchFromConnector(value || '')
                }}
              />
            </Col>

            <Col span={12}>
              <SelectInput
                name="to_id"
                label={intl.formatMessage({ id: 'inlineConnections.toConnector' })}
                options={toConnectorOptions}
                notFoundContent={
                  searchFailed.to ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                placeholder={intl.formatMessage({ id: 'inlineConnections.selectToConnector' })}
                isRequired={true}
                errorHelp={false}
                errorMsg={intl.formatMessage({ id: 'inlineConnections.toConnectorRequired' })}
                allowClear={true}
                showSearch={true}
                showLoader={toConnectorLoading}
                onInputChange={(value) => {
                  setSearchToConnector(value || '')
                }}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="from_suffix"
                label={intl.formatMessage({ id: 'inlineConnections.fromSuffix' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="to_suffix"
                label={intl.formatMessage({ id: 'inlineConnections.toSuffix' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="from_prefix"
                label={intl.formatMessage({ id: 'inlineConnections.fromPrefix' })}
                isRequired={false}
              />
            </Col>

            <Col span={12}>
              <InputText
                name="to_prefix"
                label={intl.formatMessage({ id: 'inlineConnections.toPrefix' })}
                isRequired={false}
              />
            </Col>

            <Col span={24}>
              <Flex justify="end" className="py-4">
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'inlineConnections.save' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default InlineConnectionFormModal
